import type { WarpExpandable } from "@warp-ds/elements/components/expandable";
import { Expandable } from "@warp-ds/elements/react/expandable";
import { type MouseEvent, type ReactNode, useRef } from "react";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { type EvExpandableName, evExpandableOpened } from "./evTrackingEvents.ts";

type Props = {
  title: ReactNode;
  /** Shown right-aligned next to the title, for rows that carry a spec value */
  value?: string;
  body: ReactNode;
  /** Optional "ask the seller" button, rendered under the body text */
  action?: ReactNode;
  /** Extra classes on the row wrapper, e.g. to hide a row that is shown elsewhere on wider screens */
  className?: string;
  /** Sets `object.name` on MO_ev106. Required, so no row can end up untracked by accident */
  trackingName: EvExpandableName;
};

/**
 * One expandable row inside an EV info card: a title, an optional spec value, explainer
 * body text and an optional contact action.
 */
export const EvExpandableRow = ({ title, value, body, action, className, trackingName }: Props) => {
  const { trackClick } = usePulse();
  // Warp's Expandable emits no change event, so the click that leaves the row expanded is the open.
  // Reported once per row per page load: reopening the same explainer is not a second read of it
  const hasTracked = useRef(false);

  const onClick = (event: MouseEvent<WarpExpandable>) => {
    // Warp flips `expanded` in its own listener, which runs before the event reaches us
    if (hasTracked.current || !event.currentTarget.expanded) {
      return;
    }
    hasTracked.current = true;
    // Tracking event: MO_ev106
    trackClick(evExpandableOpened(trackingName));
  };

  return (
    <div className={className}>
      <Expandable box animated onClick={onClick} className="-mx-16 part-[base]:s-bg">
        <h3 className="w-full text-m m-0 font-normal flex items-center justify-between mr-8 gap-8" slot="title">
          {title}
          {value && <span className="font-bold whitespace-nowrap">{value}</span>}
        </h3>
        <div className={`mt-0 s-text-subtle ${action ? "mb-8" : "mb-16"}`}>{body}</div>
        {action && <div className="mb-16">{action}</div>}
      </Expandable>
    </div>
  );
};
